import React, { useEffect, useState } from "react";
import { BellOff } from "lucide-react";
import axios from "axios";
import { getToken } from "../../app/auth/authConfig";

const ToolSubscriberManagement = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [removingIds, setRemovingIds] = useState([]);
  const perPage = 10;

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setIsLoading(true);
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/notifications/tools/subscriptions`,
        { headers: { "oidc-token": token } }
      );
      setSubscriptions(res.data.subscriptions || []);
    } catch (err) {
      console.error("Fetch tool subscriptions error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async (toolId, email, id) => {
    try {
      setRemovingIds((prev) => [...prev, id]);
      const token = await getToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/notifications/tools/${toolId}/subscriptions`,
        { headers: { "oidc-token": token }, data: { email } }
      );
      setSubscriptions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Remove subscription error", err);
      alert("Failed to remove subscription");
    } finally {
      setRemovingIds((prev) => prev.filter((v) => v !== id));
    }
  };

  const filtered = subscriptions.filter(
    (s) =>
      String(s.email || "").toLowerCase().includes(search.toLowerCase()) ||
      String(s.toolName || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "-";

  const btnStyle = (disabled) => ({
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    padding: "8px 12px",
    background: "#f0f0f0",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "12px",
  });

  return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Search by tool name or email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          disabled={isLoading}
          style={{
            padding: "10px 14px",
            flex: 1,
            borderRadius: "6px",
            border: "1px solid #ddd",
            backgroundColor: isLoading ? "#f0f0f0" : "white",
            cursor: isLoading ? "not-allowed" : "auto",
            fontSize: "14px",
          }}
        />
        <button
          onClick={fetchSubscriptions}
          disabled={isLoading}
          style={{
            padding: "10px 18px",
            borderRadius: "6px",
            border: "none",
            background: "#bb1ccc",
            color: "white",
            fontWeight: "bold",
            cursor: isLoading ? "not-allowed" : "pointer",
            fontSize: "14px",
          }}
        >
          {isLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
        <strong style={{ fontSize: "14px", color: "#666" }}>
          Total: {filtered.length}
        </strong>
        <div>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={isLoading || page === 1}
            style={{ ...btnStyle(isLoading || page === 1), marginRight: "8px" }}
          >
            Prev
          </button>
          <span style={{ margin: "0 8px", fontSize: "12px" }}>
            Page {page} of {Math.max(1, totalPages)}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={isLoading || page >= totalPages}
            style={{ ...btnStyle(isLoading || page >= totalPages), marginLeft: "8px" }}
          >
            Next
          </button>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 8px",
            fontSize: "14px",
            marginTop: 16,
          }}
        >
          <thead>
            <tr style={{ height: "50px" }}>
              {["🛠️ Tool Name", "📧 Subscriber Email", "📅 Subscribed Date", "⚙️ Actions"].map(
                (header, i, arr) => (
                  <th
                    key={header}
                    style={{
                      background: "#bb1ccc",
                      color: "white",
                      fontWeight: "600",
                      padding: "14px 16px",
                      textAlign: i === arr.length - 1 ? "center" : "left",
                      borderRadius:
                        i === 0
                          ? "8px 0 0 8px"
                          : i === arr.length - 1
                          ? "0 8px 8px 0"
                          : "0",
                      fontSize: "13px",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: perPage }).map((_, i) => (
                <tr key={`sk-${i}`} style={{ background: i % 2 === 0 ? "#ffffff" : "#f3f0f7" }}>
                  {[...Array(3)].map((__, j) => (
                    <td key={j} style={{ padding: "14px 16px" }}>
                      <div className="h-4 bg-gray-300 rounded w-40 animate-pulse" />
                    </td>
                  ))}
                  <td style={{ padding: "14px 16px", textAlign: "center" }}>
                    <div className="h-8 bg-gray-300 rounded w-28 mx-auto animate-pulse" />
                  </td>
                </tr>
              ))
            ) : paginated.length === 0 ? (
              <tr>
                <td
                  colSpan="4"
                  style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    background: "#f8f8f8",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ color: "#999", fontSize: "15px" }}>
                    📭 No tool subscriptions found
                  </span>
                </td>
              </tr>
            ) : (
              paginated.map((sub, idx) => (
                <tr
                  key={sub.id}
                  style={{
                    background: idx % 2 === 0 ? "#ffffff" : "#f3f0f7",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.15)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <td
                    style={{
                      padding: "14px 16px",
                      borderRadius: "8px 0 0 8px",
                      fontWeight: "600",
                      color: "#7d1fa3",
                    }}
                  >
                    {sub.toolName}
                  </td>
                  <td style={{ padding: "14px 16px" }}>{sub.email}</td>
                  <td style={{ padding: "14px 16px", color: "#666" }}>
                    {formatDate(sub.subscribedAt)}
                  </td>
                  <td
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      borderRadius: "0 8px 8px 0",
                    }}
                  >
                    <button
                      onClick={() => handleRemove(sub.toolId, sub.email, sub.id)}
                      disabled={removingIds.includes(sub.id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "7px 14px",
                        borderRadius: "6px",
                        border: "1px solid #e53e3e",
                        background: removingIds.includes(sub.id) ? "#f9f9f9" : "#fff5f5",
                        color: "#e53e3e",
                        fontWeight: "600",
                        fontSize: "13px",
                        cursor: removingIds.includes(sub.id) ? "not-allowed" : "pointer",
                        opacity: removingIds.includes(sub.id) ? 0.6 : 1,
                      }}
                    >
                      <BellOff size={14} />
                      {removingIds.includes(sub.id) ? "Removing..." : "Remove"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ToolSubscriberManagement;
